import React, { useState } from "react";
import axios from "axios";
import { ArrowRight, Loader2, CheckCircle2 } from "lucide-react";
import { useToast } from "../hooks/use-toast";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const TEAM_AREAS = ["Leadership Development", "Team Communication", "Employee Engagement", "Workplace Culture", "Business Operations and Team Growth", "Team Branding and Branded Apparel", "Not Sure Yet"];
const MEETING_TYPES = ["In-person business visit", "Virtual meeting"];
const EMPTY = { name: "", business_name: "", email: "", phone: "", location: "", business_type: "", employees: "", areas: [], meeting_type: "", challenge: "", preferred_time: "" };

export default function TeamAnalysisForm({ packageInterest, onClearPackage }) {
  const { toast } = useToast();
  const [form, setForm] = useState(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const toggleArea = (a) => setForm({ ...form, areas: form.areas.includes(a) ? form.areas.filter((x) => x !== a) : [...form.areas, a] });

  const submit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    if (!form.areas.length) return toast({ title: "Pick at least one area", description: "Choose what you'd like help with.", variant: "destructive" });
    if (!form.meeting_type) return toast({ title: "Pick a meeting type", variant: "destructive" });
    setSubmitting(true);
    try {
      const opt = (v) => v.trim() || null;
      await axios.post(`${API}/team-analysis`, { ...form, phone: opt(form.phone), employees: opt(form.employees), preferred_time: opt(form.preferred_time), package_interest: packageInterest || null });
      setSent(true);
    } catch {
      toast({ title: "Your request was not sent", description: "Please try again or email us directly.", variant: "destructive" });
    } finally {
      setSubmitting(false);
    }
  };

  if (sent) {
    return (
      <div className="bg-white rounded-2xl border border-[color:var(--dms-navy)]/10 p-10 text-center" data-testid="gt-form-success">
        <div className="w-14 h-14 rounded-full bg-[color:var(--dms-peach)] mx-auto flex items-center justify-center"><CheckCircle2 className="w-7 h-7 text-[color:var(--dms-navy)]" /></div>
        <p className="mt-5 text-[color:var(--dms-navy)]/80 max-w-lg mx-auto leading-relaxed">Thank you for your interest! Your request has been received. I'll review your information and contact you to discuss your business needs and arrange the next steps.</p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="bg-white rounded-2xl border border-[color:var(--dms-navy)]/10 p-8 md:p-10" data-testid="gt-analysis-form">
      <p className="kicker">Free business analysis</p>
      <h3 className="font-serif text-3xl text-[color:var(--dms-navy)] mt-3">Tell us about your business</h3>
      {packageInterest && (
        <p className="mt-4 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[color:var(--dms-peach-light)]/50 text-xs text-[color:var(--dms-navy)]" data-testid="gt-package-interest">
          Interested in: <b>{packageInterest}</b>
          <button type="button" onClick={onClearPackage} className="underline underline-offset-2" data-testid="gt-package-clear">change</button>
        </p>
      )}
      <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-5">
        <Field label="Full name *"><input required value={form.name} onChange={set("name")} className="gt-field" data-testid="gt-field-name" /></Field>
        <Field label="Business name *"><input required value={form.business_name} onChange={set("business_name")} className="gt-field" data-testid="gt-field-business" /></Field>
        <Field label="Email address *"><input required type="email" value={form.email} onChange={set("email")} className="gt-field" data-testid="gt-field-email" /></Field>
        <Field label="Phone number"><input value={form.phone} onChange={set("phone")} className="gt-field" data-testid="gt-field-phone" /></Field>
        <Field label="Business address or location *"><input required value={form.location} onChange={set("location")} className="gt-field" placeholder="Street, city or area" data-testid="gt-field-location" /></Field>
        <Field label="Type of business *"><input required value={form.business_type} onChange={set("business_type")} className="gt-field" placeholder="Restaurant, salon, contractor…" data-testid="gt-field-type" /></Field>
        <Field label="Number of employees"><input value={form.employees} onChange={set("employees")} className="gt-field" placeholder="e.g. 12" data-testid="gt-field-employees" /></Field>
        <Field label="Preferred day or time to be contacted"><input value={form.preferred_time} onChange={set("preferred_time")} className="gt-field" placeholder="e.g. Weekday mornings" data-testid="gt-field-time" /></Field>
      </div>

      <p className="mt-7 text-[11px] tracking-widest uppercase text-[color:var(--dms-navy)]/60 font-semibold">Which area do you need help with? *</p>
      <div className="mt-3 flex flex-wrap gap-2">
        {TEAM_AREAS.map((a, i) => (
          <button type="button" key={a} onClick={() => toggleArea(a)} data-testid={`gt-area-${i}`}
            className={`px-4 py-2 rounded-full border text-sm transition-colors ${form.areas.includes(a) ? "border-[color:var(--dms-peach)] bg-[color:var(--dms-peach-light)]/40 text-[color:var(--dms-navy)]" : "border-[color:var(--dms-navy)]/15 text-[color:var(--dms-navy)]/75 hover:border-[color:var(--dms-navy)]/35"}`}>{a}</button>
        ))}
      </div>

      <p className="mt-7 text-[11px] tracking-widest uppercase text-[color:var(--dms-navy)]/60 font-semibold">Preferred meeting type *</p>
      <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
        {MEETING_TYPES.map((m, i) => (
          <button type="button" key={m} onClick={() => setForm({ ...form, meeting_type: m })} data-testid={`gt-meeting-${i}`}
            className={`text-left p-4 rounded-xl border-2 transition-colors font-serif text-[color:var(--dms-navy)] ${form.meeting_type === m ? "border-[color:var(--dms-peach)] bg-[color:var(--dms-peach-light)]/20" : "border-[color:var(--dms-navy)]/15 hover:border-[color:var(--dms-navy)]/35"}`}>{m}</button>
        ))}
      </div>

      <div className="mt-7">
        <Field label="Your biggest business challenge *"><textarea required rows={4} value={form.challenge} onChange={set("challenge")} className="gt-field" placeholder="A brief description of what you'd like to improve" data-testid="gt-field-challenge" /></Field>
      </div>

      <div className="mt-8 flex items-center justify-between flex-wrap gap-4">
        <p className="text-xs text-[color:var(--dms-navy)]/55">No obligation to purchase a package.</p>
        <button type="submit" disabled={submitting} className="btn-peach" data-testid="gt-form-submit">
          {submitting ? <><Loader2 className="w-4 h-4 animate-spin" /> Sending…</> : <>Request My Free Analysis <ArrowRight className="w-4 h-4" /></>}
        </button>
      </div>
      <style>{`
        .gt-field { width: 100%; border: 1px solid rgba(26,47,77,0.18); background: #fff; padding: 12px 14px; border-radius: 12px; font-size: 14px; color: #1a2f4d; outline: none; transition: border-color .2s; }
        .gt-field:focus { border-color: var(--dms-peach); box-shadow: 0 0 0 3px rgba(212,165,116,0.15); }
      `}</style>
    </form>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-[11px] tracking-widest uppercase text-[color:var(--dms-navy)]/60 font-semibold">{label}</span>
      <div className="mt-2">{children}</div>
    </label>
  );
}
